import type { AxiosInstance } from 'axios';
import type {
    Configuration,
    ConfigurationNamePayload,
    GetConfigurationsRes,
    PlaceBlockPayload,
    StageFieldFlagsPayload,
    StageWritePayload,
    UpdatePlacedBlockPayload,
} from '../interfaces/configuration';
import type { UsedOptionsRes } from '../interfaces/block';

/**
 * Nav: **Configuration CRM** — pipelines, stages, placed blocks, per-stage field visible/required. See
 * docs/CRM_SECTION_CONTRACT.md §5 and §6.
 *
 * Reached as `getCrmRequests().configurations.<method>()`.
 *
 * **A team has one or more pipelines (AQ-11065), so every call below the list names one by its
 * `configurationId`.** There is no "the team's configuration" any more — `/configuration` (singular) is
 * removed, not aliased (§5).
 *
 * **Every method returns the whole configuration it wrote, because every endpoint does.** That is what
 * lets `crm/configurationCrm` have a single upsert-by-id write path shared by all three tracks — no one
 * ever patches a sub-path in state. The one exception is `remove()`, which answers `204` with nothing to
 * return.
 *
 * **A failed call rejects** — the shell's interceptor toasts the server error, then rethrows
 * (FRONTEND_PATTERNS.md) — so nothing after the `await` runs on failure, and the store's `catch` is the
 * one failure branch.
 *
 * Landed complete, with all three tracks' calls, so that nobody has to edit this shared file to add
 * their own endpoint (§6).
 */
export class ConfigurationsApi {
    protected readonly client: AxiosInstance;

    protected readonly baseURL: string;

    constructor(client: AxiosInstance, baseURL: string) {
        this.client = client;
        this.baseURL = baseURL;
    }

    private get configurationsUrl(): string {
        return `${this.baseURL}/configurations`;
    }

    private configurationUrl(configurationId: string): string {
        return `${this.configurationsUrl}/${configurationId}`;
    }

    async list(): Promise<GetConfigurationsRes> {
        const response = await this.client.get<GetConfigurationsRes>(this.configurationsUrl);
        return response.data;
    }

    async usedOptions(configurationId: string): Promise<UsedOptionsRes> {
        const response = await this.client.get<UsedOptionsRes>(`${this.configurationUrl(configurationId)}/used-options`);
        return response.data;
    }

    async create(payload: ConfigurationNamePayload): Promise<Configuration> {
        const response = await this.client.post<Configuration>(this.configurationsUrl, payload);
        return response.data;
    }

    async rename(
        configurationId: string,
        payload: ConfigurationNamePayload,
    ): Promise<Configuration> {
        const response = await this.client.patch<Configuration>(
            this.configurationUrl(configurationId),
            payload,
        );
        return response.data;
    }

    async remove(configurationId: string): Promise<void> {
        await this.client.delete(this.configurationUrl(configurationId));
    }

    /** Track D. The server names the stage (`Étape N`) and picks its colour, so there is no body. */
    async createStage(configurationId: string): Promise<Configuration> {
        const response = await this.client.post<Configuration>(
            `${this.configurationUrl(configurationId)}/stages`,
        );
        return response.data;
    }

    /** Track D. */
    async deleteStage(configurationId: string, stageId: string): Promise<Configuration> {
        const response = await this.client.delete<Configuration>(
            `${this.configurationUrl(configurationId)}/stages/${stageId}`,
        );
        return response.data;
    }

    async placeBlock(configurationId: string, payload: PlaceBlockPayload): Promise<Configuration> {
        const response = await this.client.post<Configuration>(
            `${this.configurationUrl(configurationId)}/blocks`,
            payload,
        );
        return response.data;
    }

    async updatePlacedBlock(
        configurationId: string,
        blockId: string,
        payload: UpdatePlacedBlockPayload,
    ): Promise<Configuration> {
        const response = await this.client.patch<Configuration>(
            `${this.configurationUrl(configurationId)}/blocks/${blockId}`,
            payload,
        );
        return response.data;
    }

    async removePlacedBlock(configurationId: string, blockId: string): Promise<Configuration> {
        const response = await this.client.delete<Configuration>(
            `${this.configurationUrl(configurationId)}/blocks/${blockId}`,
        );
        return response.data;
    }

    /** Track F. Identity only, and partial: `label` and/or `objective`. `type` is not writable. */
    async updateStage(
        configurationId: string,
        stageId: string,
        payload: StageWritePayload,
    ): Promise<Configuration> {
        const response = await this.client.patch<Configuration>(
            `${this.configurationUrl(configurationId)}/stages/${stageId}`,
            payload,
        );
        return response.data;
    }

    /** Track F. */
    async setStageFieldFlags(
        configurationId: string,
        stageId: string,
        payload: StageFieldFlagsPayload,
    ): Promise<Configuration> {
        const response = await this.client.patch<Configuration>(
            `${this.configurationUrl(configurationId)}/stages/${stageId}/fields`,
            payload,
        );
        return response.data;
    }
}
